import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";

// --- START: MODIFIED CONFIGURATION CONSTANTS ---

const VIEWBOX_SIZE = 900;
const N_CIRCLES = 3;

// --- USER-REQUESTED GEOMETRIC CONSTANTS ---
const FIXED_RADIUS = 160; // The radius the user wants to keep constant
const START_SIDE_LENGTH = 200; // The side length for the initial (expanded) state
const FINAL_SIDE_LENGTH = 250; // The side length for the final (coalesced/overlapping) state

// --- END: MODIFIED CONFIGURATION CONSTANTS ---

const HOVER_SCALE = 1.1;
const TSPAN_LINE_HEIGHT = 30;
const INTERACTIVE_EXTRA_ROTATION_DEGREES = 0;
const INTERACTIVE_SPIN_DURATION_MS = 2000;
const LABEL_SEQUENCE_START_DELAY_MS = 3000;
const LABEL_DISPLAY_TIME_MS = 1500;
const TOTAL_ANIMATION_DURATION_MS = 4000;
const INITIAL_ROTATION_DEGREES = 720; // 2 full rotations
const TRANSLATE_DURATION_MS = 1000;
const COALESCE_MOVEMENT_DURATION_MS = 2000;
const COALESCE_MOVEMENT_START_TIME_MS = 1000;

const IDLE_DELAY_MS = 1000; // 6 seconds wait time between repeat cycles

const ROUTE_READY_DELAY_MS = 14000; // ← 14s before route takes effect

const POSITION_TO_TOP_LEFT_OFFSET: Record<string, number> = {
  "top-left": 0,
  "top-right": -120,
  bottom: 120,
};
// --- CIRCLE AND TEXT DATA ---

const DYNAMIC_CIRCLE_DATA = [
  {
    id: "c1-svg",
    fill: "#b7b7b7", // BPO
    targetPosition: "top-left",
    label: ["BPO"],
    textFill: "#fff",
  },
  {
    id: "c3-svg",
    fill: "#0b2a59", // Data Management
    targetPosition: "top-right",
    label: ["Web", "Development"],
    textFill: "#fff",
  },
  {
    id: "c2-svg",

    fill: "#5a7fae", // Web Development
    targetPosition: "bottom",
    label: ["Address", "Finder"],
    textFill: "#fff",
  },
];

// NEW: Function to calculate positions based on the requested side length
const calculatePositionsFromSideLength = (size: number, sideLength: number) => {
    const center = size / 2;
    const HALF_SIDE_LENGTH = sideLength / 2;

    // Height (H) of an equilateral triangle: a * sqrt(3) / 2.
    const HEIGHT = (sideLength * Math.sqrt(3) / 2);

    // Calculate centroid offsets (using the geometry defined in the user's initial setup)
    const DISTANCE_BASE_TO_CENTROID = HEIGHT / 3;
    const DISTANCE_APEX_TO_CENTROID = HEIGHT * 2 / 3;

    // Define the three vertices centered around (center, center) with Apex to the right
    const positions = {
        "top-left": {
            cx: center - DISTANCE_BASE_TO_CENTROID,
            cy: center - HALF_SIDE_LENGTH
        },
        "top-right": {
            cx: center + DISTANCE_APEX_TO_CENTROID,
            cy: center
        },
        "bottom": {
            cx: center - DISTANCE_BASE_TO_CENTROID,
            cy: center + HALF_SIDE_LENGTH
        },
    };

    return positions;
};

// ------------------------------------

interface SidebarProps {
  onCircleSelect?: (id: string) => void;
  showLabels?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onCircleSelect, showLabels = false }) => {
  const location = useLocation();
  const [hoveredCircle, setHoveredCircle] = useState<string | null>(null);
  const [activeLabelIndex, setActiveLabelIndex] = useState(-1);
  const [isAnimationFinished, setIsAnimationFinished] = useState(false);
  const [coalesceProgress, setCoalesceProgress] = useState(0);

  // ← 14s delay gate for route effect
  const [isReadyForRoute, setIsReadyForRoute] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReadyForRoute(true), ROUTE_READY_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // 1. Initialized to FIXED_RADIUS (140) and will not change (no interpolation)
  const [currentRadius] = useState(FIXED_RADIUS);

  const [currentRotationAngle, setCurrentRotationAngle] = useState(0);
  const rotationRef = useRef(0);
  const interactiveAnimationRef = useRef<number | null>(null);

  const [focusedCircleId, setFocusedCircleId] = useState<string | null>(null);





  const [yTranslate, setYTranslate] = useState(-VIEWBOX_SIZE);

  const DROP_FINAL_Y = -100;

  const ROTATION_PIVOT = useMemo(
    () => `${VIEWBOX_SIZE / 2} ${VIEWBOX_SIZE / 2}`,
    []
  );
    const [enableLabelAnimation, setEnableLabelAnimation] = useState(true);

  // --- DERIVED POSITIONS based on START and FINAL side lengths ---

  const startPositions = useMemo(() => {
    return calculatePositionsFromSideLength(VIEWBOX_SIZE, START_SIDE_LENGTH + 200);
  }, []);

  const finalPositions = useMemo(() => {
    return calculatePositionsFromSideLength(VIEWBOX_SIZE, FINAL_SIDE_LENGTH);
  }, []);

  // Combine data and calculate text coordinates
  const fullData = useMemo(() => {
    return DYNAMIC_CIRCLE_DATA.map((data) => {
      const posStart =
        startPositions[data.targetPosition as keyof typeof startPositions];

      const posEnd =
        finalPositions[data.targetPosition as keyof typeof finalPositions];

      const numLines = data.label.length;

      const totalTextHeight = (numLines - 1) * TSPAN_LINE_HEIGHT;

      const verticalAdjustment = -(totalTextHeight / 2);

      return {
        ...data,
        startCx: posStart.cx,
        startCy: posStart.cy,
        finalCx: posEnd.cx,
        finalCy: posEnd.cy,
        textX: posEnd.cx,
        textY: posEnd.cy + verticalAdjustment,
        tspanX: data.label.map(() => posEnd.cx),
        tspanDy: data.label.map((_, i) => (i === 0 ? 0 : TSPAN_LINE_HEIGHT)),
      };
    });
  }, [startPositions, finalPositions]);

  const easeOutQuad = (t: number) => t * (2 - t);

  // --- INITIAL ANIMATED ROTATION & DROP-IN (React Control) ---

  useEffect(() => {
    let startTime: number;

    const rotationDuration = TOTAL_ANIMATION_DURATION_MS;

    const translateDuration = TRANSLATE_DURATION_MS;

    let initialAnimationId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;

      const elapsed = timestamp - startTime;

      const rawProgress = Math.min(1, elapsed / rotationDuration);

      const easedProgress = easeOutQuad(rawProgress);

      // 1. Rotation (runs for 4s)
      const newAngle = easedProgress * INITIAL_ROTATION_DEGREES;
      setCurrentRotationAngle(newAngle);

      // 2. Translation (runs for 1s)
      if (elapsed < translateDuration) {
        const translateProgress = elapsed / translateDuration;
        const easedTranslate = easeOutQuad(translateProgress);
        const newYTranslate =
          -VIEWBOX_SIZE + (DROP_FINAL_Y + VIEWBOX_SIZE) * easedTranslate;
        setYTranslate(newYTranslate);
      } else {
        setYTranslate(DROP_FINAL_Y); // <-- final Y position
      }

      // 3. Coalesce Movement (Position: 1s to 3s)
      if (elapsed > COALESCE_MOVEMENT_START_TIME_MS) {
        const coalesceElapsed = elapsed - COALESCE_MOVEMENT_START_TIME_MS;

        // Coalesce Progress (Position: side length 400 -> 200)
        const newCoalesceProgress = Math.min(
          1,
          coalesceElapsed / COALESCE_MOVEMENT_DURATION_MS
        );

        setCoalesceProgress(newCoalesceProgress);

        // NO RADIUS SHRINKING - currentRadius remains 140
      }

      if (rawProgress < 1) {
        initialAnimationId = requestAnimationFrame(animate);
      } else {
        // Final position correction and cleanup
        setCurrentRotationAngle(INITIAL_ROTATION_DEGREES);
        setYTranslate(DROP_FINAL_Y);
        setCoalesceProgress(1);

        // Set the interactive rotation starting point
        rotationRef.current = INITIAL_ROTATION_DEGREES;
        setIsAnimationFinished(true);
      }
    };

    initialAnimationId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(initialAnimationId);
  }, []);

  // --- CURRENT POSITION CALCULATION (Handles initial coalescence ONLY) ---

  const getCurrentPositions = useMemo(() => {
    const positions: Record<string, { cx: number; cy: number }> = {};

    fullData.forEach((data) => {
      let targetCx: number;
      let targetCy: number;

      // Interpolate between start (SIDE_LENGTH=400) and final (SIDE_LENGTH=200) positions
      targetCx =
        data.startCx + (data.finalCx - data.startCx) * coalesceProgress;

      targetCy =
        data.startCy + (data.finalCy - data.startCy) * coalesceProgress;

      positions[data.id] = { cx: targetCx, cy: targetCy };
    });

    return positions;
  }, [coalesceProgress, fullData]);

    const isTextActive = (id: string) => {
    const isActiveLabel = fullData[activeLabelIndex]?.id === id;
    const isFocused = focusedCircleId === id;
    const isHovered = hoveredCircle === id;

    return isActiveLabel || isFocused || isHovered;
    };


const sequenceTimeoutsRef = useRef<number[]>([]);
const idleTimeoutRef = useRef<number | null>(null);

const clearAllLabelTimers = () => {
  sequenceTimeoutsRef.current.forEach(clearTimeout);
  sequenceTimeoutsRef.current = [];

  if (idleTimeoutRef.current) {
    clearTimeout(idleTimeoutRef.current);
    idleTimeoutRef.current = null;
  }

  setActiveLabelIndex(-1);
};

useEffect(() => {
  if (!enableLabelAnimation) return;

  let timeouts: number[] = [];

  const runSequence = () => {
    const cycle = Array.from({ length: N_CIRCLES }, (_, i) => i).concat([-1]);

    cycle.forEach((index, i) => {
      const t = window.setTimeout(() => {
        if (!hoveredCircle && !focusedCircleId) {
          setActiveLabelIndex(index);
        }
      }, LABEL_DISPLAY_TIME_MS * i);
      timeouts.push(t);
    });

    return cycle.length * LABEL_DISPLAY_TIME_MS; // total sequence duration
  };

  const startProcess = () => {
    if (!isAnimationFinished) {
      const t = window.setTimeout(startProcess, LABEL_SEQUENCE_START_DELAY_MS);
      timeouts.push(t);
      return;
    }

    //
    //  1. Run first sequence
    //
    const seq1Duration = runSequence();

    //
    //  2. Wait 2 seconds, then run second sequence
    //
    const t1 = window.setTimeout(() => {
      const seq2Duration = runSequence();

    }, seq1Duration + 3000); // <-- changed to 2 seconds

    timeouts.push(t1);
  };

  startProcess();

  return () => {
    timeouts.forEach(clearTimeout);
  };
}, [
  enableLabelAnimation,
  isAnimationFinished,
  hoveredCircle,
 focusedCircleId,
]);




  const initialOrder = fullData.map((c) => c.id);

  const [circleOrder, setCircleOrder] = useState<string[]>(initialOrder);

  const getCircleData = (id: string) => fullData.find((c) => c.id === id)!;

  // --- INTERACTIVE ANIMATION FUNCTION (JS Driven, uses easeOutQuad) ---

  const animateInteractiveSpin = useCallback(
    (startAngle: number, targetAngle: number) => {
      let startTime: number;

      if (interactiveAnimationRef.current) {
        cancelAnimationFrame(interactiveAnimationRef.current);
      }

      const spin = (timestamp: number) => {
        if (!startTime) startTime = timestamp;

        const elapsed = timestamp - startTime;

        const rawProgress = Math.min(1, elapsed / INTERACTIVE_SPIN_DURATION_MS);

        const easedProgress = easeOutQuad(rawProgress);

        const newAngle =
          startAngle + (targetAngle - startAngle) * easedProgress;

        setCurrentRotationAngle(newAngle);

        rotationRef.current = newAngle; // Update ref every frame

        if (rawProgress < 1) {
          interactiveAnimationRef.current = requestAnimationFrame(spin);
        } else {
          // Animation finished: ensure final angle is set and clean up

          setCurrentRotationAngle(targetAngle);

          rotationRef.current = targetAngle;

          interactiveAnimationRef.current = null;
        }
      };

      interactiveAnimationRef.current = requestAnimationFrame(spin);
    },
    [easeOutQuad]
  );

   useEffect(() => {
  // ← Wait for both animation finish AND 14s delay
  if (!isAnimationFinished || !isReadyForRoute) return;

  const routeToCircle: Record<string, string> = {
    "/bpo": "c1-svg",
    "/address-finder": "c2-svg",
    "/web-development": "c3-svg",
  };

  const matched = Object.keys(routeToCircle).find((path) =>
    location.pathname.startsWith(path)
  );

  if (!matched) {
    // Home or non-cloud route
    setFocusedCircleId(null);
    setEnableLabelAnimation(true);
    return;
  }

  const circleId = routeToCircle[matched];

  if (focusedCircleId === circleId) return;

  const data = getCircleData(circleId);
  const itemPosition = data.targetPosition;
  const requiredOffsetToTarget =
    POSITION_TO_TOP_LEFT_OFFSET[itemPosition];

  const startAngle = rotationRef.current;

  let spinDelta = requiredOffsetToTarget - (startAngle % 360);

  if (spinDelta > 180) spinDelta -= 360;
  if (spinDelta < -180) spinDelta += 360;

  const targetAngle = startAngle + spinDelta;

  animateInteractiveSpin(startAngle, targetAngle);

  rotationRef.current = targetAngle;

  setFocusedCircleId(circleId);
  setEnableLabelAnimation(false);
  setActiveLabelIndex(-1);

}, [
  location.pathname,
  isAnimationFinished,
  isReadyForRoute,
  focusedCircleId,
  animateInteractiveSpin,
  getCircleData,
]);

  // --- INTERACTION HANDLERS ---

  const handleMouseEnter = (hoveredId: string) => {
    if (!isAnimationFinished) return;

    clearAllLabelTimers();
    setCircleOrder((prevOrder) => {
      if (prevOrder[prevOrder.length - 1] === hoveredId) return prevOrder;

      const newOrder = prevOrder.filter((id) => id !== hoveredId);

      newOrder.push(hoveredId);

      return newOrder;
    });

    setHoveredCircle(hoveredId);
  };

  const startIdleSequence = () => {
  if (!isAnimationFinished || !enableLabelAnimation) return;

  const runSequence = () => {
    const cycle = Array.from({ length: N_CIRCLES }, (_, i) => i).concat([-1]);

    cycle.forEach((index, i) => {
      const t = window.setTimeout(() => {
        if (!hoveredCircle && !focusedCircleId) {
          setActiveLabelIndex(index);
        }
      }, LABEL_DISPLAY_TIME_MS * i);

      sequenceTimeoutsRef.current.push(t);
    });

    const endTimeout = window.setTimeout(() => {
      sequenceTimeoutsRef.current = [];
      // schedule next idle check
      idleTimeoutRef.current = window.setTimeout(runSequence, IDLE_DELAY_MS);
    }, (cycle.length * LABEL_DISPLAY_TIME_MS) + 50);

    sequenceTimeoutsRef.current.push(endTimeout);
  };

  runSequence();
};


  const handleMouseLeave = () => {
    if (!isAnimationFinished) return;
    setCircleOrder(initialOrder);
    clearAllLabelTimers();
    startIdleSequence();
    setHoveredCircle(null);
  };






 const handleCircleClick = useCallback(
  (id: string) => {
    if (!isAnimationFinished) return;

    // 1. Stop all label timers
    clearAllLabelTimers();  // <-- this will clear pending timeouts

    // 2. Disable automatic label cycling
    setEnableLabelAnimation(false);
    setActiveLabelIndex(-1);

    // 3. Proceed with your focus / rotation logic
    if (onCircleSelect) onCircleSelect(id);

    const data = getCircleData(id);
    const itemPosition = data.targetPosition;
    const requiredOffsetToTarget = POSITION_TO_TOP_LEFT_OFFSET[itemPosition];

    const isAlreadyFocused = focusedCircleId === id;
    const startAngle = rotationRef.current;

    let targetAngle;
    let newFocusId;

    if (interactiveAnimationRef.current) {
      cancelAnimationFrame(interactiveAnimationRef.current);
      interactiveAnimationRef.current = null;
    }

    if (isAlreadyFocused) {
      // UNFOCUS
      newFocusId = null;
      targetAngle = startAngle;
      setCurrentRotationAngle(targetAngle);
      rotationRef.current = targetAngle;
    } else {
      // FOCUS
      let spinDelta = requiredOffsetToTarget - (startAngle % 360);
      if (spinDelta > 180) spinDelta -= 360;
      if (spinDelta < -180) spinDelta += 360;

      const extraSpinDirection =
        spinDelta >= 0
          ? INTERACTIVE_EXTRA_ROTATION_DEGREES
          : -INTERACTIVE_EXTRA_ROTATION_DEGREES;

      targetAngle = startAngle + spinDelta + extraSpinDirection;
      newFocusId = id;
      animateInteractiveSpin(startAngle, targetAngle);
    }

    setFocusedCircleId(newFocusId);
  },
  [
    isAnimationFinished,
    onCircleSelect,
    focusedCircleId,
    animateInteractiveSpin,
    getCircleData,
  ]
);


  const isTextVisible = (id: string) => {
    // ← FIXED: sequential labels always show on ALL pages (including home)
    const isActiveInSequence = fullData[activeLabelIndex]?.id === id;
    if (isActiveInSequence) return true;

    // Hover always works
    if (hoveredCircle === id) return true;

    // Focused (click/route) only when showLabels is on
    if (!showLabels) return false;

    return focusedCircleId === id;
  };




  const getCenteredScaleTransform = (data: (typeof fullData)[0]) => {
    if (hoveredCircle === data.id && isAnimationFinished) {
      const currentPos = getCurrentPositions[data.id];

      const cx = currentPos.cx;

      const cy = currentPos.cy;

      return `translate(${cx}, ${cy}) scale(${HOVER_SCALE}) translate(-${cx}, -${cy})`;
    }

    return "scale(1)";
  }; // --- TRANSFORMATION VARIABLES ---

  const rotatingGroupTransform = useMemo(() => {
    return `translate(0, ${yTranslate})`;
  }, [yTranslate]);

  const spinGroupTransform = useMemo(() => {
    return `rotate(${currentRotationAngle}, ${ROTATION_PIVOT})`;
  }, [currentRotationAngle, ROTATION_PIVOT]);

    return (
        <div className="">
        <div className="relative z-[101]">
        <svg
            id="animated-svg"
            viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
            className={`
            w-[350px] h-[440px]        /* mobile (default) */
            sm:w-[400px] sm:h-[400px] /* ≥640px */
            md:w-[500px] md:h-[500px] /* ≥768px */
            lg:w-[650px] lg:h-[650px] /* ≥1024px */
            xl:w-[800px] xl:h-[800px] /* ≥1280px */
            ${isAnimationFinished ? "" : "cursor-default"}
            `}

            >

          {/* OUTER GROUP: Handles vertical translation (drop-in) */}
          <g id="rotating-group" transform={rotatingGroupTransform}>
            {/* INNER GROUP: Handles all rotation (spin) */}
            <g id="spin-group" transform={spinGroupTransform}>
              {circleOrder.map((id) => {
                const data = getCircleData(id);
                const currentPos = getCurrentPositions[data.id];

                const isInteractive =
                  isAnimationFinished && coalesceProgress === 1;

                return (
                  <circle
                    key={data.id}
                    id={data.id}
                    className={
                      isInteractive ? "cursor-pointer" : "cursor-default"
                    }
                    cx={currentPos.cx}
                    cy={currentPos.cy}
                    r={currentRadius}
                    fill={data.fill}
                    style={{
                      outline: "none",
                      pointerEvents: isInteractive ? "auto" : "none",
                      transition: isInteractive ? "transform 0.3s, fill 0.3s" : "none", // Add fill transition
                      filter: `drop-shadow(0 4px 10px ${data.fill}99)`,
                      // Style to indicate focus

                    }}
                    onMouseEnter={
                      isInteractive
                        ? () => handleMouseEnter(data.id)
                        : undefined
                    }
                    onMouseLeave={isInteractive ? handleMouseLeave : undefined}
                    onClick={
                      isInteractive
                        ? () => handleCircleClick(data.id)
                        : undefined
                    }
                    transform={getCenteredScaleTransform(data)}
                  >
                  </circle>
                );
              })}

              {/* Text elements */}
              {fullData.map((data) => {
                const { cx, cy } = getCurrentPositions[data.id];

                return (
                  <text
                    key={data.id}
                    x={cx}
                    y={data.textY + (cy - data.finalCy)}
                    textAnchor="middle"
                    fill={data.textFill}
                    fontSize={
                        isTextVisible(data.id)
                        ? (isTextActive(data.id) ? 27 : 18)
                        : 16     // 👈 shrink before fading out
                    }
                    fontWeight="bold"
                    opacity={isTextVisible(data.id) ? 1 : 0}
                    style={{
                        transition: `
                        opacity 0.3s ease-out,
                        font-size 0.25s ease-out
                        `,
                        pointerEvents: "none",
                    }}
                    transform={`rotate(${-currentRotationAngle}, ${cx}, ${cy})`}
                    >


                    {data.label.map((line, i) => (
                      <tspan key={i} x={cx} dy={data.tspanDy[i]}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                );
              })}
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default Sidebar;